import { gql } from 'graphql-request';

import { FRAGMENT_LISTING_STATS, FRAGMENT_MSA_PREVIEW } from '@hcs/cerberus';

import {
  FRAGMENT_MSA_HCRI_CORE,
  FRAGMENT_ZIP_HCRI_CORE,
} from './fragments/Hcri.graphql';
import {
  FRAGMENT_MSA_HPI_CORE,
  FRAGMENT_MSA_HPI_TIME_SERIES,
  FRAGMENT_ZIP_HPI_CORE,
  FRAGMENT_ZIP_HPI_TIME_SERIES,
} from './fragments/Hpi.graphql';
import {
  FRAGMENT_POPULATION,
  FRAGMENT_POPULATION_TIME_SERIES,
  FRAGMENT_UNEMPLOYMENT_RATE_TIME_SERIES,
} from './fragments/PopulationStats.graphql';
import {
  FRAGMENT_MSA_RPI_CORE,
  FRAGMENT_RPI_TIME_SERIES,
  FRAGMENT_ZIP_RPI_CORE,
} from './fragments/Rpi.graphql';
import {
  FRAGMENT_ZIP_CORE,
  FRAGMENT_ZIP_GEOM,
  FRAGMENT_ZIP_MARKET_QUALITY,
} from './fragments/Zip.graphql';

export const QUERY_MSA_INSIGHTS_PREVIEW = gql`
  # HC CAPABILITY: platform-search-experience
  ${FRAGMENT_MSA_PREVIEW}
  query MsaInsightsPreview($msaId: String) {
    lookupMsaDetails(id: { msaId: $msaId }) {
      ...MsaPreview
    }
  }
`;

export const QUERY_ZIP_INSIGHTS_PREVIEW = gql`
  # HC CAPABILITY: platform-search-experience
  ${FRAGMENT_ZIP_CORE}
  query ZipInsightsPreview($zipcode: String) {
    lookupZipDetails(id: { zipcode: $zipcode }) {
      ...ZipCore
    }
  }
`;

export const QUERY_MSA_DETAILS_ALL_MSAS = gql`
  # HC CAPABILITY: market-insights-product-access
  ${FRAGMENT_MSA_PREVIEW}
  ${FRAGMENT_LISTING_STATS}
  ${FRAGMENT_MSA_HPI_CORE}
  ${FRAGMENT_MSA_HCRI_CORE}
  ${FRAGMENT_MSA_RPI_CORE}
  query AllMsaDetails($propertyType: PropertyType) {
    allMsaDetails {
      ...MsaPreview
      listingStats {
        latestStats {
          ...ListingStats
        }
      }
      rentalListingStats {
        latestStats {
          ...ListingStats
        }
      }
      hpi {
        ...MsaHpiCore
      }
      hcri {
        ...MsaHcriCore
      }
      rpi(propertyType: $propertyType) {
        ...MsaRpiCore
        forecastCalcs(propertyType: $propertyType) {
          risk1YearLoss
          return1Year
        }
      }
      populationStats {
        population {
          ...PopulationCore
        }
      }
    }
  }
`;

export const QUERY_MSA_DETAILS = gql`
  # HC CAPABILITY: market-insights-product-access
  ${FRAGMENT_MSA_PREVIEW}
  ${FRAGMENT_LISTING_STATS}
  ${FRAGMENT_MSA_HPI_CORE}
  ${FRAGMENT_MSA_HCRI_CORE}
  ${FRAGMENT_MSA_RPI_CORE}
  ${FRAGMENT_POPULATION}
  query MsaDetails($msaId: String, $propertyType: PropertyType) {
    lookupMsaDetails(id: { msaId: $msaId }) {
      ...MsaPreview
      listingStats {
        latestStats {
          ...ListingStats
        }
      }
      rentalListingStats {
        latestStats {
          ...ListingStats
        }
      }
      listingStatsCondoTownhouse {
        latestStats {
          ...ListingStats
        }
      }
      rentalListingStatsCondoTownhouse {
        latestStats {
          ...ListingStats
        }
      }
      hpi {
        ...MsaHpiCore
      }
      hcri {
        ...MsaHcriCore
      }
      rpi(propertyType: $propertyType) {
        ...MsaRpiCore
        forecastCalcs(propertyType: $propertyType) {
          risk1YearLoss
          return1Year
        }
      }
      populationStats {
        population {
          ...PopulationCore
        }
      }
    }
  }
`;

export const QUERY_MSA_LISTING_STATS_TIME_SERIES = gql`
  # HC CAPABILITY: market-insights-product-access
  ${FRAGMENT_MSA_PREVIEW}
  ${FRAGMENT_LISTING_STATS}
  query MsaListingStatsTimeSeries(
    $msaId: String
    $fromDate: Date
    $toDate: Date
  ) {
    lookupMsaDetails(id: { msaId: $msaId }) {
      ...MsaPreview
      listingStats {
        timeSeries(fromDate: $fromDate, toDate: $toDate, order: ASCENDING) {
          ...ListingStats
        }
      }
      rentalListingStats {
        timeSeries(fromDate: $fromDate, toDate: $toDate, order: ASCENDING) {
          ...ListingStats
        }
      }
      listingStatsCondoTownhouse {
        timeSeries(fromDate: $fromDate, toDate: $toDate, order: ASCENDING) {
          ...ListingStats
        }
      }
      rentalListingStatsCondoTownhouse {
        timeSeries(fromDate: $fromDate, toDate: $toDate, order: ASCENDING) {
          ...ListingStats
        }
      }
    }
  }
`;

export const QUERY_MSA_HPI_TIME_SERIES = gql`
  # HC CAPABILITY: market-insights-product-access
  ${FRAGMENT_MSA_PREVIEW}
  ${FRAGMENT_MSA_HPI_TIME_SERIES}
  query MsaHpiTimeSeries($msaId: String, $fromDate: Date, $toDate: Date) {
    lookupMsaDetails(id: { msaId: $msaId }) {
      ...MsaPreview
      hpi {
        historicalTimeSeries: timeSeries(
          seriesType: HISTORICAL
          fromDate: $fromDate
          toDate: $toDate
          order: ASCENDING
        ) {
          ...MsaHpiTimeSeries
        }
        forecastTimeSeries: timeSeries(
          seriesType: FORECAST
          fromDate: $fromDate
          toDate: $toDate
          order: ASCENDING
        ) {
          ...MsaHpiTimeSeries
        }
      }
    }
  }
`;

export const QUERY_MSA_RPI_TIME_SERIES = gql`
  # HC CAPABILITY: market-insights-product-access
  ${FRAGMENT_MSA_PREVIEW}
  ${FRAGMENT_RPI_TIME_SERIES}
  query MsaRpiTimeSeries(
    $msaId: String
    $fromDate: Date
    $toDate: Date
    $propertyType: PropertyTypeEnum
  ) {
    lookupMsaDetails(id: { msaId: $msaId }) {
      ...MsaPreview
      rpi(propertyTypeEnum: $propertyType) {
        historicalTimeSeries: timeSeries(
          seriesType: HISTORICAL
          fromDate: $fromDate
          toDate: $toDate
          order: ASCENDING
          propertyTypeEnum: $propertyType
        ) {
          ...RpiTimeSeries
        }
        forecastTimeSeries: timeSeries(
          seriesType: FORECAST
          fromDate: $fromDate
          toDate: $toDate
          order: ASCENDING
          propertyTypeEnum: $propertyType
        ) {
          ...RpiTimeSeries
        }
      }
    }
  }
`;

export const QUERY_ZIP_RPI_TIME_SERIES = gql`
  # HC CAPABILITY: market-insights-product-access
  ${FRAGMENT_ZIP_CORE}
  ${FRAGMENT_RPI_TIME_SERIES}
  query ZipRpiTimeSeries(
    $zipcode: String
    $fromDate: Date
    $toDate: Date
    $propertyType: PropertyTypeEnum
  ) {
    lookupZipDetails(id: { zipcode: $zipcode }) {
      ...ZipCore
      rpi(propertyTypeEnum: $propertyType) {
        historicalTimeSeries: timeSeries(
          seriesType: HISTORICAL
          fromDate: $fromDate
          toDate: $toDate
          order: ASCENDING
          propertyTypeEnum: $propertyType
        ) {
          ...RpiTimeSeries
        }
        forecastTimeSeries: timeSeries(
          seriesType: FORECAST
          fromDate: $fromDate
          toDate: $toDate
          order: ASCENDING
          propertyTypeEnum: $propertyType
        ) {
          ...RpiTimeSeries
        }
      }
    }
  }
`;

export const QUERY_MSA_UNEMPLOYMENT_TIME_SERIES = gql`
  # HC CAPABILITY: market-insights-product-access
  ${FRAGMENT_MSA_PREVIEW}
  ${FRAGMENT_UNEMPLOYMENT_RATE_TIME_SERIES}
  query MsaUnemploymentTimeSeries(
    $msaId: String
    $fromDate: Date
    $toDate: Date
  ) {
    lookupMsaDetails(id: { msaId: $msaId }) {
      ...MsaPreview
      populationStats {
        unemploymentRate {
          timeSeries(fromDate: $fromDate, toDate: $toDate, order: ASCENDING) {
            ...UnemploymentRateTimeSeries
          }
        }
      }
    }
  }
`;

export const QUERY_MSA_POPULATION_TIME_SERIES = gql`
  # HC CAPABILITY: market-insights-product-access
  ${FRAGMENT_MSA_PREVIEW}
  ${FRAGMENT_POPULATION_TIME_SERIES}
  query MsaPopulationTimeSeries(
    $msaId: String
    $fromDate: Date
    $toDate: Date
  ) {
    lookupMsaDetails(id: { msaId: $msaId }) {
      ...MsaPreview
      populationStats {
        population {
          timeSeries(fromDate: $fromDate, toDate: $toDate, order: ASCENDING) {
            ...PopulationTimeSeries
          }
        }
      }
    }
  }
`;

export const QUERY_MSA_ZIPS_LIST = gql`
  # HC CAPABILITY: aexp-product-access
  query MsaZips($msaId: String) {
    lookupMsaDetails(id: { msaId: $msaId }) {
      zips {
        zipcode
      }
    }
  }
`;

export const QUERY_ZIP_GEOM = gql`
  # HC CAPABILITY: market-insights-product-access
  ${FRAGMENT_ZIP_CORE}
  ${FRAGMENT_ZIP_GEOM}
  query ZipGeom($zipcode: String) {
    lookupZipDetails(id: { zipcode: $zipcode }) {
      ...ZipCore
      ...ZipGeom
    }
  }
`;

export const QUERY_MSA_ALL_ZIP_DETAILS = gql`
  # HC CAPABILITY: market-insights-product-access
  ${FRAGMENT_ZIP_CORE}
  ${FRAGMENT_ZIP_HCRI_CORE}
  ${FRAGMENT_ZIP_HPI_CORE}
  ${FRAGMENT_ZIP_RPI_CORE}
  ${FRAGMENT_LISTING_STATS}
  ${FRAGMENT_ZIP_MARKET_QUALITY}
  query AllMsaZipDetails($msaId: String, $propertyType: PropertyType) {
    lookupMsaDetails(id: { msaId: $msaId }) {
      zips {
        ...ZipCore
        listingStats {
          latestStats {
            ...ListingStats
          }
        }
        hpi {
          ...ZipHpiCore
        }
        hcri {
          ...ZipHcriCore
        }
        rpi(propertyType: $propertyType) {
          ...ZipRpiCore
          forecastCalcs(propertyType: $propertyType) {
            risk1YearLoss
            return1Year
          }
        }
        marketQuality {
          ...ZipMarketQuality
        }
      }
    }
  }
`;

export const QUERY_ZIP_DETAILS = gql`
  # HC CAPABILITY: market-insights-product-access
  ${FRAGMENT_ZIP_CORE}
  ${FRAGMENT_ZIP_HCRI_CORE}
  ${FRAGMENT_ZIP_HPI_CORE}
  ${FRAGMENT_ZIP_RPI_CORE}
  ${FRAGMENT_LISTING_STATS}
  ${FRAGMENT_ZIP_MARKET_QUALITY}
  query ZipDetails($zipcode: String, $propertyType: PropertyType) {
    lookupZipDetails(id: { zipcode: $zipcode }) {
      ...ZipCore
      listingStats {
        latestStats {
          ...ListingStats
        }
      }
      listingStatsCondoTownhouse {
        latestStats {
          ...ListingStats
        }
      }
      hpi {
        ...ZipHpiCore
      }
      hcri {
        ...ZipHcriCore
      }
      rpi(propertyType: $propertyType) {
        ...ZipRpiCore
        forecastCalcs(propertyType: $propertyType) {
          risk1YearLoss
          return1Year
        }
      }
      marketQuality {
        ...ZipMarketQuality
      }
    }
  }
`;

export const QUERY_ZIP_LISTING_STATS_TIME_SERIES = gql`
  # HC CAPABILITY: market-insights-product-access
  ${FRAGMENT_LISTING_STATS}
  query ZipListingStatsTimeSeries(
    $zipcode: String
    $fromDate: Date
    $toDate: Date
  ) {
    lookupZipDetails(id: { zipcode: $zipcode }) {
      listingStats {
        timeSeries(fromDate: $fromDate, toDate: $toDate, order: ASCENDING) {
          ...ListingStats
        }
      }
      listingStatsCondoTownhouse {
        timeSeries(fromDate: $fromDate, toDate: $toDate, order: ASCENDING) {
          ...ListingStats
        }
      }
    }
  }
`;

export const QUERY_ZIP_HPI_TIME_SERIES = gql`
  # HC CAPABILITY: market-insights-product-access
  ${FRAGMENT_ZIP_HPI_TIME_SERIES}
  query ZipHpiTimeSeries($zipcode: String, $fromDate: Date, $toDate: Date) {
    lookupZipDetails(id: { zipcode: $zipcode }) {
      hpi {
        historicalTimeSeries: timeSeries(
          seriesType: HISTORICAL
          fromDate: $fromDate
          toDate: $toDate
          order: ASCENDING
        ) {
          ...ZipHpiTimeSeries
        }
        forecastTimeSeries: timeSeries(
          seriesType: FORECAST
          fromDate: $fromDate
          toDate: $toDate
          order: ASCENDING
        ) {
          ...ZipHpiTimeSeries
        }
      }
    }
  }
`;
